import { getAudioContext } from '../audioUnlock';
import { chunkForSynthesis } from '../piper/chunk';
import { abandonPending, synthesiseChunk, usePiper } from '../piper/usePiper';
import { playBuffer } from '../playBuffer';
import { ProviderUnavailable, type SpeakOpts, type SpeakResult, type TtsProvider } from '../TtsProvider';

/**
 * Piper, on the device (2.8).
 *
 * Sits below `azureTts` and above `webSpeech`, which is the whole point: when the network is gone the person keeps
 * a real voice instead of dropping to the platform's. Phase 0 measured 603 ms to first audio for ten words on
 * this iPad against a ~900 ms budget, so this is a fallback that does not feel like one.
 *
 * ── Chunking with lookahead is the entire design ──
 *
 * Piper has no streaming API: `run()` resolves with the whole utterance, so latency scales with length — 2036 ms
 * for 35 words. So the sentence is split (`chunk.ts`), and while chunk N plays, chunk N+1 is synthesised. At
 * RTF 0.19 producing a chunk costs about a fifth of its own playback, so the audio never catches up. The person
 * hears the first words in the time it takes to make *those words*, not the whole sentence.
 *
 * The seams are inaudible for free, because `chunk.ts` puts them at commas — where a listener expects a pause.
 * That is why sequential `playBuffer` calls suffice and there is no audio-clock scheduling here.
 *
 * ── No `prerender` ──
 *
 * Deliberately absent. `prerenderMissing` fills the audio cache, `cachedAudio` sits **above** `azureTts`, and so a
 * phrase rendered by Piper would be spoken by Piper for ever — including online, where the better voice is one
 * request away. The pre-render pass stays Azure's.
 */
export function createPiperLocalProvider(): TtsProvider {
  return {
    id: 'piperLocal',

    /**
     * Available whenever the model is loaded — **not** only when offline.
     *
     * If Azure fails for any reason at all, a rate limit, a 500, a lapsed subscription, then this is the right
     * next step. Second-guessing that with `navigator.onLine` would mean falling through to the platform voice
     * while a perfectly good one sat in the cache.
     */
    isAvailable(): boolean {
      const state = usePiper.getState();
      return state.ready && !state.failed;
    },

    async speak(text: string, opts: SpeakOpts): Promise<SpeakResult> {
      const startedAtMs = Date.now();
      const ctx = getAudioContext();
      if (!ctx) throw new ProviderUnavailable('piperLocal', 'no_audio_context');

      const chunks = chunkForSynthesis(text);
      if (chunks.length === 0) throw new ProviderUnavailable('piperLocal', 'empty_text');

      let silent = false;
      // The *first* chunk that made a sound is time-to-first-audio for the whole sentence; the
      // later ones are the lookahead working. Kept as the first reading rather than the last.
      let audioAtMs: number | undefined;

      // The lookahead. Chunk 1 is awaited — that wait *is* time-to-first-audio — and every later chunk is already
      // in flight by the time its turn comes.
      let inFlight = synthesiseChunk(chunks[0]!);

      for (let index = 0; index < chunks.length; index += 1) {
        if (opts.signal.aborted) {
          abandonPending();
          break;
        }

        let audio: { pcm: Float32Array<ArrayBuffer>; sampleRate: number };
        try {
          audio = await inFlight;
        } catch (err) {
          // A failure on the first chunk means nothing was spoken, so the chain should try the next provider. A
          // failure later means the person has already been heard in part; stopping quietly is better than
          // starting the whole sentence again in a different voice.
          if (index === 0) {
            throw new ProviderUnavailable(
              'piperLocal',
              err instanceof Error ? err.message : 'synthesis_failed',
            );
          }
          console.warn('[piper] chunk failed mid-sentence', { index });
          break;
        }

        // Start the next chunk *before* playing this one, so synthesis and playback overlap.
        const next = chunks[index + 1];
        inFlight = next === undefined ? Promise.reject(new Error('done')) : synthesiseChunk(next);
        // Nothing awaits that rejection when the sentence ends here, and an unhandled rejection would be noise in
        // the console at the end of every utterance.
        inFlight.catch(() => {});

        const result = await playBuffer(toAudioBuffer(ctx, audio), opts.signal);
        if (result.silent) silent = true;
        if (audioAtMs === undefined && result.startedPlayingAtMs !== undefined) {
          audioAtMs = result.startedPlayingAtMs;
        }
        if (result.aborted) {
          abandonPending();
          break;
        }
      }

      // The lookahead always leaves one chunk in flight that the end of the sentence made pointless.
      // Its deadline would otherwise fire a second later and mark a perfectly healthy worker dead
      // (`SPK-20`) — at the end of every long sentence, not rarely.
      abandonPending();

      return {
        providerId: 'piperLocal',
        startedAtMs,
        cached: false,
        ...(silent ? { silent: true } : {}),
        ...(audioAtMs === undefined ? {} : { audioAtMs }),
      };
    },
  };
}

/**
 * Float samples into an `AudioBuffer`, with no decode step at all.
 *
 * The model emits raw mono PCM and the config gives its sample rate, so there is no WAV to wrap and nothing for
 * `decodeAudioData` to parse — the 3 ms Phase 0 measured for decode simply does not happen here. The buffer is
 * created at the model's own rate; Web Audio resamples to the output device.
 */
function toAudioBuffer(
  ctx: AudioContext,
  audio: { pcm: Float32Array<ArrayBuffer>; sampleRate: number },
): AudioBuffer {
  const buffer = ctx.createBuffer(1, audio.pcm.length, audio.sampleRate);
  buffer.copyToChannel(audio.pcm, 0);
  return buffer;
}
